"use client";

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { trpcApi } from "@lattice/frontend/lib/trpc/client/index";
import { useParams } from "next/navigation";
import { Sparkles } from "lucide-react";

interface T_InlineSummaryProps {
  section: string;
  is_crypto: boolean;
  isVisible?: boolean;
  symbol?: string;
}

const getSectionName = (section: string): string => {
  const sectionNames: Record<string, string> = {
    overview: "Price",
    analysts: "Analyst Ratings",
    news: "News",
    statistics: "Statistics",
    "insider-trades": "Insider Trades",
    "trading-trends": "Trading Trends",
  };
  return sectionNames[section] || section;
};

export default function InlineSummary({
  section,
  isVisible = false,
  symbol: propSymbol,
  is_crypto,
}: T_InlineSummaryProps) {
  const params = useParams();
  const rawSymbol = params?.stock_symbol;
  const symbol = propSymbol || (typeof rawSymbol === "string" ? rawSymbol : "");
  const [displayedText, setDisplayedText] = React.useState("");
  const [isTyping, setIsTyping] = React.useState(false);
  const [hasTyped, setHasTyped] = React.useState(false);
  const [loadingPhase, setLoadingPhase] = React.useState(0);
  const [isHidden, setIsHidden] = React.useState(false);
  const [isMounted, setIsMounted] = React.useState(false);

  // Ensure component is mounted before showing dynamic content
  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  // Consistent spacing for all sections - 6 units bottom margin
  const marginClass = "mb-6";

  const { data, isLoading, error } = trpcApi.stock.get_summary.useQuery(
    { symbol, is_crypto },
    {
      enabled: !!symbol,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
    },
  );

  const summaryText = React.useMemo(() => {
    if (!data || !(section in data)) return null;
    const entry = Object.entries(data).find(([k]) => k === section);
    const rawText = entry?.[1];

    // Ensure we have a valid string
    if (typeof rawText === "string" && rawText.trim()) {
      return rawText.trim();
    }

    return null;
  }, [data, section]);

  // Animate loading message between "summarizing" and "analyzing" - only on client
  React.useEffect(() => {
    if (isMounted && (isLoading || (!summaryText && !error))) {
      const interval = setInterval(() => {
        setLoadingPhase((prev) => (prev + 1) % 2);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [isMounted, isLoading, summaryText, error]);

  // Typing animation - only depends on summaryText and isVisible
  React.useEffect(() => {
    if (isVisible && summaryText && !hasTyped) {
      setIsTyping(true);
      setDisplayedText("");

      let currentIndex = 0;
      let timeoutId: NodeJS.Timeout;
      const typingSpeed = 8; // milliseconds per character for smoother typing

      // Use setTimeout instead of setInterval for more control
      const typeNextChar = () => {
        if (currentIndex < summaryText.length) {
          // Use functional setState to ensure we're always working with the latest text
          setDisplayedText(() => {
            // Ensure we're building the text correctly
            const nextText = summaryText.substring(0, currentIndex + 1);
            return nextText;
          });
          currentIndex++;
          timeoutId = setTimeout(typeNextChar, typingSpeed);
        } else {
          setIsTyping(false);
          setHasTyped(true);
          // Set the full text one more time to ensure completeness
          setDisplayedText(summaryText);
        }
      };

      // Start typing with a small delay to ensure state is ready
      timeoutId = setTimeout(typeNextChar, 100);

      return () => {
        // Cleanup if component unmounts
        if (timeoutId) clearTimeout(timeoutId);
      };
    }
  }, [isVisible, summaryText, hasTyped, section]);

  // Reset hasTyped when section changes to allow re-typing
  React.useEffect(() => {
    setHasTyped(false);
    setDisplayedText("");
  }, [section]);

  // Don't render animations until mounted to avoid hydration issues
  if (!isMounted) {
    return null; // Return nothing during SSR since summaries are shown by default
  }

  return (
    <AnimatePresence mode="wait">
      {isHidden ? (
        <motion.div
          key="show-button"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className={`overflow-hidden ${marginClass}`}
        >
          <button
            onClick={() => setIsHidden(false)}
            className="text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors flex items-center gap-2"
          >
            <Sparkles className="w-3 h-3" />
            <span>Show Summary</span>
          </button>
        </motion.div>
      ) : (
        <motion.div
          key="summary-content"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{
            opacity: 0,
            height: 0,
          }}
          transition={{
            duration: 0.2,
            ease: "easeOut",
          }}
          className="overflow-hidden"
        >
          {renderSummaryContent()}
        </motion.div>
      )}
    </AnimatePresence>
  );

  function renderSummaryContent() {
    // Show loading state with alternating "summarizing" and "analyzing" message
    if (isLoading || (!summaryText && !error)) {
      // Use a static message on first render to avoid hydration mismatch
      const loadingMessage = !isMounted
        ? `Prism is summarizing ${symbol}'s ${getSectionName(section)}`
        : loadingPhase === 0
          ? `Prism is summarizing ${symbol}'s ${getSectionName(section)}`
          : `Prism is analyzing ${symbol}'s ${getSectionName(section)}`;

      return (
        <div
          className={`bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/10 backdrop-blur-sm shadow-sm rounded-lg relative overflow-hidden ${marginClass}`}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-purple-500/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-3 h-3 text-primary" />
              <span className="text-xs font-medium text-foreground/80 uppercase tracking-wider">
                Prism Summary
              </span>
            </div>
            <button
              onClick={() => setIsHidden(true)}
              className="text-[10px] text-muted-foreground/50 hover:text-muted-foreground transition-colors"
            >
              HIDE
            </button>
          </div>

          {/* Content area with shimmer */}
          <div className="p-4 relative">
            {/* Subtle shimmer animation overlay */}
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_3s_infinite]">
              <div className="h-full w-full bg-gradient-to-r from-transparent via-white/[0.03] to-transparent" />
            </div>

            <div className="relative z-10">
              <p className="text-gray-400 text-sm leading-relaxed">
                {loadingMessage}
                <span className="inline-block ml-1">
                  <span className="animate-[bounce_1.5s_ease-in-out_infinite]">
                    .
                  </span>
                  <span className="animate-[bounce_1.5s_ease-in-out_infinite_100ms]">
                    .
                  </span>
                  <span className="animate-[bounce_1.5s_ease-in-out_infinite_200ms]">
                    .
                  </span>
                </span>
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Show error state
    if (error) {
      console.error("Error loading summary:", error);
      return null;
    }

    // Don't show if no summary text after loading
    if (!summaryText) {
      return null;
    }

    return (
      <div
        className={`bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/10 backdrop-blur-sm shadow-sm rounded-lg relative ${marginClass}`}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-purple-500/15 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3 h-3 text-primary" />
            <span className="text-xs font-medium text-foreground/80 uppercase tracking-wider">
              Prism Summary
            </span>
          </div>
          <button
            onClick={() => setIsHidden(true)}
            className="text-[10px] text-muted-foreground/50 hover:text-muted-foreground transition-colors"
          >
            HIDE
          </button>
        </div>

        {/* Content area */}
        <div className="p-4 relative">
          <div className="relative z-10">
            <p className="text-foreground/90 text-sm leading-relaxed whitespace-pre-wrap">
              {displayedText}
              {isTyping && (
                <span className="inline-block w-0.5 h-4 bg-primary ml-0.5 animate-pulse" />
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }
}
